import type { AxiosProgressEvent } from 'axios';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { AzureBlobFileDto } from '@/ApiClient';
import { useApi } from '@lib/api';
import { ClientError, errorMessages } from '@lib/client-error';
import { signalrListen } from '@lib/signalr';
import { useUser } from '@/store/user';
import { ensureFileFormat } from '@lib/files';

type ProgressListener = (percentage: number) => void;

function uploadProgress(progress?: ProgressListener) {
  return (progressEvent: AxiosProgressEvent) => {
    const percentage = parseInt(Math.round((progressEvent.loaded * 100) / (progressEvent.total ?? 1)).toString(10), 10);

    progress?.(percentage);
  };
}

export const useAzureBlobFiles = defineStore('azure-blob-files', () => {
  const files = ref<Record<number, AzureBlobFileDto>>({});
  const loadingIds = ref<number[]>([]);
  const isUploading = ref(false);
  const api = useApi();

  function get(id: number): AzureBlobFileDto | undefined {
    return files.value[id];
  }

  function getByIds(ids: number[]): AzureBlobFileDto[] {
    return ids.map((id) => get(id)).filter((file): file is AzureBlobFileDto => file != null);
  }

  // Payloads carrying a file arrive from several places at once — a page's own load, a BOM replayed from
  // its cache, a SignalR update — and not in the order they were produced. Keep whichever knows about the
  // newest version, so a payload built before an upload can't undo it. Same newest version means the same
  // content with possibly fresher naming, so that one is taken.
  function cache(items: AzureBlobFileDto[]) {
    for (const item of items) {
      const known = files.value[item.id];
      if (known && newestVersionId(known) > newestVersionId(item)) continue;

      files.value[item.id] = item;
    }
  }

  function newestVersionId(file: AzureBlobFileDto) {
    return file.versions.reduce((newest, version) => Math.max(newest, version.id), 0);
  }

  // Batching: when a table mounts, every row's files cell requests its ids in the same
  // tick — collect them and issue one /files/list request instead of one per cell.
  let batchIds: Set<number> | null = null;
  let batchPromise: Promise<void> | null = null;

  async function load(ids: number[]): Promise<AzureBlobFileDto[]> {
    const missingIds = [...new Set(ids)].filter((id) => !files.value[id]);
    if (missingIds.length > 0) {
      if (!batchIds) {
        const idsToLoad = (batchIds = new Set<number>());
        batchPromise = new Promise((resolve, reject) => {
          setTimeout(() => {
            batchIds = null; // calls from here on start a new batch
            api.file_GetList(useUser().brandId, [...idsToLoad]).then((loadedFiles) => {
              cache(loadedFiles);
              resolve();
            }, reject);
          });
        });
      }
      for (const id of missingIds) batchIds.add(id);
      await batchPromise;
    }

    return getByIds(ids);
  }

  // The server rejects a zero-length part too — this only saves the round trip and names the file the
  // user actually picked.
  function rejectEmptyFile(file: File) {
    if (file.size === 0) throw new ClientError(errorMessages.upload_file_empty);
  }

  async function upload(file: File, progress?: ProgressListener): Promise<AzureBlobFileDto> {
    rejectEmptyFile(file);
    isUploading.value = true;
    try {
      // The extension is dropped server-side, in AzureBlobFileService.Create — doing it here as well cost
      // "Look 2.1.jpg" its ".1".
      const uploadedFile = await api.file_Upload(useUser().brandId, file.name, file, {
        onUploadProgress: uploadProgress(progress),
      });
      cache([uploadedFile]);
      return uploadedFile;
    } finally {
      isUploading.value = false;
    }
  }

  // A picture, rather than any file: rejected if it isn't one, and HEIC is converted to something
  // browsers can draw before it goes up. Dimensions are read server-side from the uploaded bytes.
  async function uploadImage(file: File, progress?: ProgressListener): Promise<AzureBlobFileDto> {
    return upload(await ensureFileFormat(file), progress);
  }

  async function addVersionFromImage(
    fileId: number,
    file: File,
    progress?: ProgressListener
  ): Promise<AzureBlobFileDto> {
    return addVersion(fileId, await ensureFileFormat(file), progress);
  }

  async function addVersion(fileId: number, file: File, progress?: ProgressListener): Promise<AzureBlobFileDto> {
    rejectEmptyFile(file);
    loadingIds.value.push(fileId);
    try {
      const updatedFile = await api.file_AddVersion(useUser().brandId, fileId, file, {
        onUploadProgress: uploadProgress(progress),
      });
      cache([updatedFile]);
      return updatedFile;
    } finally {
      loadingIds.value = loadingIds.value.filter((id) => id !== fileId);
    }
  }

  async function updateName(fileId: number, name: string): Promise<AzureBlobFileDto> {
    const updatedFile = await api.file_UpdateName(useUser().brandId, fileId, { name });
    cache([updatedFile]);
    return updatedFile;
  }

  function downloadLatestUrl(fileId: number): string {
    return `/api/brand/${useUser().brandId}/files/${fileId}/download-latest-version`;
  }

  function downloadVersionUrl(versionId: number): string {
    return `/api/brand/${useUser().brandId}/files/versions/${versionId}/download`;
  }

  function downloadPreviewUrl(versionId: number): string {
    return `/api/brand/${useUser().brandId}/files/versions/${versionId}/download-preview`;
  }

  function listen() {
    signalrListen(
      'NewAzureBlobFileUpdate',
      (file: AzureBlobFileDto) => {
        cache([file]);
      },
      { listenForever: true }
    );
  }

  function clear() {
    files.value = {};
    loadingIds.value = [];
    isUploading.value = false;
  }

  return {
    files,
    loadingIds,
    isUploading,
    get,
    getByIds,
    cache,
    load,
    upload,
    uploadImage,
    addVersion,
    addVersionFromImage,
    updateName,
    downloadLatestUrl,
    downloadVersionUrl,
    downloadPreviewUrl,
    listen,
    clear,
  };
});
